/**
 * SecurityPage — Dashboard Security Settings
 * ════════════════════════════════════════════
 * Shared by MentorDashboard and MenteeDashboard via route:
 *   /dashboard/mentor/security
 *   /dashboard/mentee/security
 *
 * Sections:
 *   1. Two-Factor Authentication (TOTP) — enable / disable
 *   2. Active Sessions / Devices
 *   3. Password Change
 *   4. Security Notifications
 *   5. Login Activity Log
 *   6. Account Danger Zone
 *
 * Real API endpoints (via securityService):
 *   GET  /users/me/sessions             → active device sessions
 *   DELETE /users/me/sessions/:id       → revoke a session
 *   DELETE /users/me/sessions/all       → sign out all other devices
 *   GET  /users/me/login-activity       → last 10 login events
 *   PUT  /users/me/password             → { currentPassword, newPassword }
 *   GET  /users/me/notifications/security
 *   PUT  /users/me/notifications/security
 *   GET  /users/me/export
 *   DELETE /users/me                    → delete account
 *   POST /2fa/setup / verify-setup / disable  (handled in TwoFactorSetup)
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import TwoFactorSetup from '../../components/TwoFactorSetup';
import securityService from '../../services/securityService';

// ── Derive current browser session from navigator ──────────────
function buildCurrentSession() {
  const ua = navigator.userAgent;
  let browser = 'Browser';
  if (ua.includes('Edg'))                                browser = 'Edge';
  else if (ua.includes('Chrome') && !ua.includes('OPR')) browser = 'Chrome';
  else if (ua.includes('Firefox'))                       browser = 'Firefox';
  else if (ua.includes('Safari'))                        browser = 'Safari';
  else if (ua.includes('OPR'))                           browser = 'Opera';

  let os = '';
  if      (ua.includes('iPhone'))    os = 'iPhone';
  else if (ua.includes('iPad'))      os = 'iPad';
  else if (ua.includes('Android'))   os = 'Android';
  else if (ua.includes('Macintosh')) os = 'Mac';
  else if (ua.includes('Windows'))   os = 'Windows';
  else if (ua.includes('Linux'))     os = 'Linux';

  const device = os ? `${browser} on ${os}` : browser;
  const now    = new Date();
  const time   = 'Today, ' + now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return { id: '__current__', device, location: 'Current location', ip: '—', time, current: true };
}

// ── Toggle switch ──────────────────────────────────────────────
function Toggle({ enabled, onToggle, size = 'md', disabled }) {
  const w = size === 'sm' ? 36 : 48;
  const h = size === 'sm' ? 20 : 26;
  const d = size === 'sm' ? 14 : 18;
  const pad = 3;
  return (
    <div onClick={disabled ? undefined : onToggle}
      style={{ width: w, height: h, borderRadius: 100, cursor: disabled ? 'default' : 'pointer', flexShrink: 0,
        background: enabled ? '#1DB954' : '#d1d5db', opacity: disabled ? 0.5 : 1,
        position: 'relative', transition: 'background .2s' }}>
      <div style={{ position: 'absolute', top: pad, left: enabled ? w - d - pad : pad, width: d, height: d,
        borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .2s' }} />
    </div>
  );
}

// ── Section card wrapper ───────────────────────────────────────
function Section({ icon, title, subtitle, children, danger }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: danger ? '1.5px solid #fecaca' : '1px solid #e5e7eb', marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: danger ? '#fef2f2' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>{icon}</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: '.92rem', color: danger ? '#dc2626' : '#111827' }}>{title}</div>
          {subtitle && <div style={{ fontSize: '.72rem', color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: '18px 22px' }}>{children}</div>
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────
function StatusBadge({ on }) {
  return (
    <span style={{ padding: '3px 10px', borderRadius: 100, fontSize: '.68rem', fontWeight: 800,
      background: on ? '#f0fdf4' : '#f9fafb', color: on ? '#16a34a' : '#6b7280',
      border: `1px solid ${on ? '#bbf7d0' : '#e5e7eb'}` }}>
      {on ? '✅ Enabled' : '○ Disabled'}
    </span>
  );
}

// ── Skeleton row ───────────────────────────────────────────────
function SkeletonRow() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e5e7eb' }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 12, width: '55%', background: '#e5e7eb', borderRadius: 6, marginBottom: 6 }} />
        <div style={{ height: 10, width: '75%', background: '#f3f4f6', borderRadius: 6 }} />
      </div>
    </div>
  );
}

export default function SecurityPage() {
  const { user, updateUser, logout } = useAuth();

  // 2FA
  const [tfaData, setTfaData] = useState(user?.twoFactor || { enabled: false });
  const [showTfaModal, setShowTfaModal] = useState(false);

  // Password change
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwOk, setPwOk] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirm: false });

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [signOutAllLoading, setSignOutAllLoading] = useState(false);

  // Login Activity
  const [loginActivity, setLoginActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // Notifications
  const [notifs, setNotifs] = useState({
    newLogin: true,
    newDevice: true,
    passwordChange: true,
    payoutRequest: user?.role === 'mentor',
  });
  const [notifsSaving, setNotifsSaving] = useState(false);

  // Danger Zone
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Load sessions on mount — always show at least current device
  useEffect(() => {
    setSessionsLoading(true);
    securityService.getSessions()
      .then(res => {
        const loaded = res.sessions || [];
        // If API doesn't mark any session as current, inject the browser session at the top
        if (!loaded.some(s => s.current)) {
          setSessions([buildCurrentSession(), ...loaded]);
        } else {
          setSessions(loaded);
        }
      })
      .catch(() => setSessions([buildCurrentSession()]))
      .finally(() => setSessionsLoading(false));
  }, []);

  // ── Load login activity on mount ───────────────────────────
  useEffect(() => {
    setActivityLoading(true);
    securityService.getLoginActivity()
      .then(res => setLoginActivity(res.activity || []))
      .catch(() => setLoginActivity([]))
      .finally(() => setActivityLoading(false));
  }, []);

  // ── Sync 2FA from user object ──────────────────────────────
  useEffect(() => {
    if (user?.twoFactor) setTfaData(user.twoFactor);
  }, [user?.twoFactor]);

  const handleTfaUpdate = (patch) => {
    const updated = { ...tfaData, ...patch.twoFactor };
    setTfaData(updated);
    // TODO §SECURITY: backend must enforce 2FA before issuing tokens — local state is UI-only
    if (updateUser) updateUser({ twoFactor: updated });
  };

  const handlePasswordChange = async () => {
    setPwError('');
    if (!pwForm.current)                       return setPwError('Current password is required.');
    if (pwForm.newPw.length < 8)               return setPwError('New password must be at least 8 characters.');
    if (pwForm.newPw !== pwForm.confirm)        return setPwError('Passwords do not match.');
    setPwLoading(true);
    try {
      await securityService.changePassword(pwForm.current, pwForm.newPw);
      setPwOk(true);
      setPwForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => setPwOk(false), 4000);
    } catch (err) {
      setPwError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleNotifToggle = async (key) => {
    const updated = { ...notifs, [key]: !notifs[key] };
    setNotifs(updated);
    setNotifsSaving(true);
    try {
      await securityService.updateSecurityNotifications(updated);
    } catch {} finally {
      setNotifsSaving(false);
    }
  };

  const revokeSession = async (id) => {
    setRevoking(id);
    try {
      await securityService.revokeSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch {} finally {
      setRevoking(null);
    }
  };

  const handleSignOutAll = async () => {
    setSignOutAllLoading(true);
    try {
      await securityService.revokeAllSessions();
      setSessions(prev => prev.filter(s => s.current));
    } catch {} finally {
      setSignOutAllLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    setExportDone(false);
    try {
      const res = await securityService.exportData();
      if (res.downloadUrl) {
        window.open(res.downloadUrl, '_blank');
      } else {
        setExportDone(true);
        setTimeout(() => setExportDone(false), 6000);
      }
    } catch {} finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (!deletePassword) return setDeleteError('Please enter your password to confirm.');
    setDeleteLoading(true);
    try {
      await securityService.deleteAccount(deletePassword);
      await logout();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  const pwStrength = (pw) => {
    if (!pw) return null;
    let score = 0;
    if (pw.length >= 8)          score++;
    if (pw.length >= 12)         score++;
    if (/[A-Z]/.test(pw))        score++;
    if (/[0-9]/.test(pw))        score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { label: 'Weak',   color: '#ef4444', width: '33%' };
    if (score <= 3) return { label: 'Fair',   color: '#f59e0b', width: '55%' };
    if (score <= 4) return { label: 'Good',   color: '#3b82f6', width: '78%' };
    return              { label: 'Strong', color: '#16a34a', width: '100%' };
  };
  const strength = pwStrength(pwForm.newPw);

  return (
    <div style={{ maxWidth: 680 }}>
      <h1 className="db-page-title">Security</h1>
      <p className="db-page-sub">Manage your account security, two-factor authentication, and active sessions.</p>

      {/* ── Security Score ─────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e)', borderRadius: 16, padding: '20px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: tfaData.enabled ? '#1DB954' : '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', flexShrink: 0 }}>
          {tfaData.enabled ? '🔐' : '⚠️'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: '.98rem', marginBottom: 4 }}>
            Security Score: <span style={{ color: tfaData.enabled ? '#1DB954' : '#f59e0b' }}>{tfaData.enabled ? 'Strong' : 'Moderate'}</span>
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {[
              { label: 'Password', ok: true },
              { label: '2FA',      ok: tfaData.enabled },
              { label: 'Email',    ok: user?.isEmailVerified !== false },
              { label: 'Profile',  ok: (user?.profileComplete || 0) >= 50 },
            ].map(item => (
              <span key={item.label} style={{ padding: '2px 8px', borderRadius: 100, fontSize: '.62rem', fontWeight: 700,
                background: item.ok ? 'rgba(29,185,84,.2)' : 'rgba(255,255,255,.08)',
                color: item.ok ? '#1DB954' : 'rgba(255,255,255,.4)',
                border: `1px solid ${item.ok ? 'rgba(29,185,84,.3)' : 'rgba(255,255,255,.1)'}` }}>
                {item.ok ? '✓' : '○'} {item.label}
              </span>
            ))}
          </div>
        </div>
        {!tfaData.enabled && (
          <button onClick={() => setShowTfaModal(true)}
            style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: '#f59e0b', color: '#fff', fontFamily: 'inherit', fontWeight: 800, fontSize: '.78rem', cursor: 'pointer', flexShrink: 0 }}>
            Enable 2FA →
          </button>
        )}
      </div>

      {/* ── 1. Two-Factor Authentication ────────────────────── */}
      <Section icon="🔐" title="Two-Factor Authentication (2FA)"
        subtitle="Require a one-time code from your Authenticator app at every login">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <StatusBadge on={tfaData.enabled} />
              {tfaData.enabled && tfaData.enabledAt && (
                <span style={{ fontSize: '.68rem', color: '#9ca3af' }}>
                  Since {new Date(tfaData.enabledAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
            <div style={{ fontSize: '.78rem', color: '#6b7280', lineHeight: 1.65, marginBottom: 12 }}>
              {tfaData.enabled
                ? 'TOTP is active. Your account requires an Authenticator app code at every login.'
                : 'Protect your account with Google Authenticator, Authy, or Microsoft Authenticator.'}
            </div>
            {tfaData.enabled && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                <span style={{ padding: '3px 10px', borderRadius: 100, background: '#e8f0fe', color: '#2563eb', fontSize: '.68rem', fontWeight: 700 }}>📱 TOTP App</span>
                <span style={{ padding: '3px 10px', borderRadius: 100, background: '#f0fdf4', color: '#16a34a', fontSize: '.68rem', fontWeight: 700 }}>💾 {tfaData.backupCodesCount || 10} backup codes</span>
              </div>
            )}
            <button onClick={() => setShowTfaModal(true)}
              style={{ padding: '9px 18px', borderRadius: 10, border: tfaData.enabled ? '1.5px solid #fecaca' : 'none',
                background: tfaData.enabled ? '#fff' : '#1DB954',
                color: tfaData.enabled ? '#dc2626' : '#fff',
                fontFamily: 'inherit', fontWeight: 800, fontSize: '.8rem', cursor: 'pointer' }}>
              {tfaData.enabled ? '🔓 Disable 2FA' : '🔐 Enable 2FA'}
            </button>
          </div>

          <div style={{ background: '#f9fafb', borderRadius: 12, padding: '14px', minWidth: 160 }}>
            <div style={{ fontSize: '.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.07em', color: '#9ca3af', marginBottom: 10 }}>Compatible Apps</div>
            {[
              { name: 'Google Authenticator', badge: 'Recommended' },
              { name: 'Authy',               badge: 'Multi-device' },
              { name: 'Microsoft Auth',       badge: 'Corporate'   },
              { name: '1Password',            badge: 'Built-in'    },
            ].map(a => (
              <div key={a.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7, gap: 8 }}>
                <span style={{ fontSize: '.73rem', fontWeight: 600, color: '#374151' }}>{a.name}</span>
                <span style={{ padding: '1px 7px', borderRadius: 100, background: '#e8f0fe', color: '#2563eb', fontSize: '.58rem', fontWeight: 700, flexShrink: 0 }}>{a.badge}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── 2. Password ─────────────────────────────────────── */}
      <Section icon="🔑" title="Change Password" subtitle="Use a strong, unique password you don't use elsewhere">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { key: 'current', label: 'Current Password',   placeholder: 'Enter current password' },
            { key: 'newPw',   label: 'New Password',        placeholder: 'Min 8 chars, 1 number, 1 symbol' },
            { key: 'confirm', label: 'Confirm New Password', placeholder: 'Repeat new password' },
          ].map(f => (
            <div key={f.key}>
              <label style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: '#374151', marginBottom: 5 }}>{f.label}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw[f.key] ? 'text' : 'password'}
                  value={pwForm[f.key]}
                  onChange={e => setPwForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ width: '100%', padding: '10px 40px 10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10,
                    fontFamily: 'inherit', fontSize: '.85rem', boxSizing: 'border-box', outline: 'none',
                    borderColor: f.key === 'confirm' && pwForm.confirm && pwForm.confirm !== pwForm.newPw ? '#ef4444' : '#e5e7eb' }}
                />
                <button onClick={() => setShowPw(p => ({ ...p, [f.key]: !p[f.key] }))}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '.8rem' }}>
                  {showPw[f.key] ? '🙈' : '👁'}
                </button>
              </div>
              {f.key === 'newPw' && strength && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ height: 3, background: '#f3f4f6', borderRadius: 100, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: strength.width, background: strength.color, borderRadius: 100, transition: 'all .3s' }} />
                  </div>
                  <div style={{ fontSize: '.63rem', fontWeight: 700, color: strength.color, marginTop: 3 }}>{strength.label}</div>
                </div>
              )}
              {f.key === 'confirm' && pwForm.confirm && pwForm.confirm !== pwForm.newPw && (
                <div style={{ fontSize: '.68rem', color: '#ef4444', marginTop: 3 }}>⚠️ Passwords do not match</div>
              )}
            </div>
          ))}

          {pwError && <div style={{ padding: '9px 13px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 9, fontSize: '.78rem', color: '#dc2626', fontWeight: 600 }}>⚠️ {pwError}</div>}
          {pwOk    && <div style={{ padding: '9px 13px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9, fontSize: '.78rem', color: '#16a34a', fontWeight: 700 }}>✅ Password updated successfully</div>}

          <button onClick={handlePasswordChange} disabled={pwLoading}
            style={{ padding: '11px', borderRadius: 10, border: 'none', background: '#1a1a2e', color: '#fff', fontFamily: 'inherit', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer', marginTop: 4 }}>
            {pwLoading
              ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,.35)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
                  Updating…
                </span>
              : '🔑 Update Password'}
          </button>
        </div>
      </Section>

      {/* ── 3. Security Notifications ───────────────────────── */}
      <Section icon="🔔" title="Security Notifications" subtitle="Get alerted when important security events happen">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { key: 'newLogin',       label: 'New login detected',        sub: 'Email when someone logs into your account' },
            { key: 'newDevice',      label: 'New device or location',     sub: 'Email when login happens from an unrecognised device' },
            { key: 'passwordChange', label: 'Password changed',           sub: 'Email when your password is updated' },
            ...(user?.role === 'mentor' ? [{ key: 'payoutRequest', label: 'Payout request processed', sub: 'Email when your payout is completed' }] : []),
          ].map(n => (
            <div key={n.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #f3f4f6', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '.84rem', fontWeight: 700, color: '#111827' }}>{n.label}</div>
                <div style={{ fontSize: '.7rem', color: '#9ca3af', marginTop: 2 }}>{n.sub}</div>
              </div>
              <Toggle enabled={notifs[n.key]} onToggle={() => handleNotifToggle(n.key)} size="sm" disabled={notifsSaving} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: '.68rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6 }}>
          {notifsSaving
            ? <><span style={{ width: 10, height: 10, border: '1.5px solid #d1d5db', borderTopColor: '#9ca3af', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} /> Saving…</>
            : 'Changes save automatically'}
        </div>
      </Section>

      {/* ── 4. Active Sessions ──────────────────────────────── */}
      <Section icon="💻" title="Active Sessions" subtitle="Devices currently logged into your account">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sessionsLoading
            ? [1, 2, 3].map(i => <SkeletonRow key={i} />)
            : sessions.length === 0
              ? <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: '.82rem' }}>No active sessions found.</div>
              : sessions.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: '#f9fafb', borderRadius: 10, border: `1px solid ${s.current ? '#bbf7d0' : '#f3f4f6'}` }}>
                  <div style={{ fontSize: '1.3rem', flexShrink: 0 }}>
                    {s.device.includes('iPhone') || s.device.includes('Android') ? '📱' : '💻'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '.82rem', color: '#111827' }}>{s.device}</span>
                      {s.current && <span style={{ padding: '1px 7px', borderRadius: 100, background: '#f0fdf4', color: '#16a34a', fontSize: '.6rem', fontWeight: 800, border: '1px solid #bbf7d0' }}>Current</span>}
                    </div>
                    <div style={{ fontSize: '.7rem', color: '#9ca3af', marginTop: 2 }}>
                      📍 {s.location} · {s.ip} · {s.time}
                    </div>
                  </div>
                  {!s.current && (
                    <button onClick={() => revokeSession(s.id)} disabled={revoking === s.id}
                      style={{ padding: '5px 12px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff', color: '#dc2626', fontFamily: 'inherit', fontWeight: 700, fontSize: '.72rem', cursor: 'pointer', flexShrink: 0 }}>
                      {revoking === s.id ? '…' : 'Revoke'}
                    </button>
                  )}
                </div>
              ))
          }
          <button onClick={handleSignOutAll} disabled={signOutAllLoading || sessionsLoading}
            style={{ padding: '9px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#dc2626', fontFamily: 'inherit', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', marginTop: 4, opacity: signOutAllLoading ? 0.6 : 1 }}>
            {signOutAllLoading
              ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ width: 12, height: 12, border: '2px solid #fecaca', borderTopColor: '#dc2626', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
                  Signing out…
                </span>
              : '🚪 Sign Out All Other Devices'}
          </button>
        </div>
      </Section>

      {/* ── 5. Login Activity ───────────────────────────────── */}
      <Section icon="📋" title="Recent Login Activity" subtitle="Last 10 login events on your account">
        {activityLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map(i => <SkeletonRow key={i} />)}
          </div>
        ) : loginActivity.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: '.82rem' }}>No login activity found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {['Device', 'Location', 'IP', 'Time', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: '#9ca3af', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loginActivity.map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                    <td style={{ padding: '9px 8px', color: '#374151', fontWeight: 600 }}>{s.device}</td>
                    <td style={{ padding: '9px 8px', color: '#6b7280' }}>{s.location}</td>
                    <td style={{ padding: '9px 8px', color: '#9ca3af', fontFamily: 'monospace', fontSize: '.72rem' }}>{s.ip}</td>
                    <td style={{ padding: '9px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>{s.time}</td>
                    <td style={{ padding: '9px 8px' }}>
                      {s.status === 'failed'
                        ? <span style={{ color: '#ef4444', fontWeight: 700, fontSize: '.7rem' }}>Failed</span>
                        : s.current
                          ? <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '.7rem' }}>Active</span>
                          : <span style={{ color: '#9ca3af', fontSize: '.7rem' }}>Success</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: '.68rem', color: '#9ca3af' }}>
          Showing last {loginActivity.length} events · Full log available via API
        </div>
      </Section>

      {/* ── 6. Danger Zone ──────────────────────────────────── */}
      <Section icon="⚠️" title="Danger Zone" subtitle="Irreversible actions — proceed with caution" danger>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Export Data */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.84rem', color: '#374151' }}>Export My Data</div>
              <div style={{ fontSize: '.71rem', color: '#9ca3af', marginTop: 2 }}>Download all your data — GDPR compliant</div>
            </div>
            <button onClick={handleExportData} disabled={exportLoading}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: '.75rem', cursor: 'pointer', color: '#374151', opacity: exportLoading ? 0.6 : 1 }}>
              {exportLoading ? '⏳ Preparing…' : '📦 Export Data'}
            </button>
          </div>
          {exportDone && (
            <div style={{ padding: '9px 13px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9, fontSize: '.78rem', color: '#16a34a', fontWeight: 600 }}>
              ✅ Export requested — a download link will be sent to your email.
            </div>
          )}

          {/* Delete Account */}
          <div style={{ borderTop: '1px solid #fee2e2', paddingTop: 12 }}>
            {!showDeleteConfirm ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '.84rem', color: '#dc2626' }}>Delete Account</div>
                  <div style={{ fontSize: '.71rem', color: '#9ca3af', marginTop: 2 }}>Permanently delete your account and all data</div>
                </div>
                <button onClick={() => setShowDeleteConfirm(true)}
                  style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fef2f2', fontFamily: 'inherit', fontWeight: 700, fontSize: '.75rem', cursor: 'pointer', color: '#dc2626' }}>
                  🗑 Delete Account
                </button>
              </div>
            ) : (
              <div style={{ background: '#fef2f2', borderRadius: 10, padding: '14px 16px', border: '1px solid #fecaca' }}>
                <div style={{ fontWeight: 800, fontSize: '.84rem', color: '#dc2626', marginBottom: 6 }}>⚠️ This cannot be undone</div>
                <div style={{ fontSize: '.73rem', color: '#b91c1c', marginBottom: 12, lineHeight: 1.6 }}>
                  All your data — sessions, bookings, messages, and profile — will be permanently deleted. Enter your password to confirm.
                </div>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #fecaca', borderRadius: 9, fontFamily: 'inherit', fontSize: '.85rem', boxSizing: 'border-box', outline: 'none', marginBottom: 10, background: '#fff' }}
                />
                {deleteError && (
                  <div style={{ fontSize: '.74rem', color: '#dc2626', fontWeight: 600, marginBottom: 10 }}>⚠️ {deleteError}</div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}
                    style={{ flex: 1, padding: '9px', borderRadius: 9, border: '1.5px solid #e5e7eb', background: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', color: '#374151' }}>
                    Cancel
                  </button>
                  <button onClick={handleDeleteAccount} disabled={deleteLoading}
                    style={{ flex: 1, padding: '9px', borderRadius: 9, border: 'none', background: '#dc2626', fontFamily: 'inherit', fontWeight: 800, fontSize: '.78rem', cursor: 'pointer', color: '#fff', opacity: deleteLoading ? 0.7 : 1 }}>
                    {deleteLoading
                      ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <span style={{ width: 11, height: 11, border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
                          Deleting…
                        </span>
                      : '🗑 Confirm Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* ── 2FA Modal ───────────────────────────────────────── */}
      {showTfaModal && (
        <TwoFactorSetup
          user={{ ...user, twoFactor: tfaData }}
          onUpdate={handleTfaUpdate}
          onClose={() => setShowTfaModal(false)}
        />
      )}
    </div>
  );
}
